import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  CreateProgramPayload,
  UpdateProgramPayload,
  Location,
  LocationsResponse,
  Program,
  ProgramDetail,
  ProgramsResponse,
  ProgramType,
  ProgramTypesResponse,
  SubstantiveArea,
  SubstantiveAreasResponse,
} from '../../shared/models/program.models';
import { PageParams } from '../../shared/models/pagination.models';

@Injectable({ providedIn: 'root' })
export class ProgramService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // ── Programs ──────────────────────────────────────────────────────────────

  getPrograms(params?: PageParams, programTypeId?: string, substantiveAreaId?: string) {
    let httpParams = params ? this.toHttpParams(params) : new HttpParams();
    if (programTypeId) httpParams = httpParams.set('programTypeId', programTypeId);
    if (substantiveAreaId) httpParams = httpParams.set('substantiveAreaId', substantiveAreaId);
    return this.http.get<ProgramsResponse>(`${this.apiUrl}/api/programs`, { params: httpParams });
  }

  getProgramById(id: string) {
    return this.http.get<ProgramDetail>(`${this.apiUrl}/api/programs/${id}`);
  }

  createProgram(payload: CreateProgramPayload) {
    return this.http.post<Program>(`${this.apiUrl}/api/programs`, payload);
  }

  updateProgram(payload: UpdateProgramPayload) {
    return this.http.put<Program>(`${this.apiUrl}/api/programs`, payload);
  }

  deleteProgram(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/api/programs/${id}`);
  }

  // ── Locations ─────────────────────────────────────────────────────────────

  getLocations() {
    return this.http.get<LocationsResponse>(`${this.apiUrl}/api/locations`);
  }

  // ── Person Types ──────────────────────────────────────────────────────────

  getPIInvolvementTypes() {
    return this.http.get<{ id: string; name: string }[]>(`${this.apiUrl}/api/pi-involvement-types`);
  }

  // ── Program Types ─────────────────────────────────────────────────────────

  getProgramTypes() {
    return this.http.get<ProgramTypesResponse>(`${this.apiUrl}/api/program-types`);
  }

  getProgramTypeById(id: string) {
    return this.http.get<ProgramType>(`${this.apiUrl}/api/program-types/${id}`);
  }

  createProgramType(payload: Omit<ProgramType, 'id'>) {
    return this.http.post<ProgramType>(`${this.apiUrl}/api/program-types`, payload);
  }

  updateProgramType(payload: ProgramType) {
    return this.http.put<ProgramType>(`${this.apiUrl}/api/program-types`, payload);
  }

  deleteProgramType(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/api/programs-types/${id}`);
  }

  // ── Substantive Areas ─────────────────────────────────────────────────────

  getSubstantiveAreas(params?: PageParams) {
    return this.http.get<SubstantiveAreasResponse>(`${this.apiUrl}/api/substantive-area`, {
      params: params ? this.toHttpParams(params) : undefined,
    });
  }

  getSubstantiveAreaById(id: string) {
    return this.http.get<SubstantiveArea>(`${this.apiUrl}/api/substantive-area/${id}`);
  }

  createSubstantiveArea(payload: Omit<SubstantiveArea, 'id'>) {
    return this.http.post<SubstantiveArea>(`${this.apiUrl}/api/substantive-area`, payload);
  }

  updateSubstantiveArea(payload: SubstantiveArea) {
    return this.http.put<SubstantiveArea>(`${this.apiUrl}/api/substantive-area`, payload);
  }

  deleteSubstantiveArea(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/api/substantive-area/${id}`);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private toHttpParams(p: PageParams): HttpParams {
    return new HttpParams()
      .set('pageSize', p.pageSize ?? 10)
      .set('pageIndex', p.pageIndex ?? 0)
      .set('filter', p.filter ?? '');
  }
}
